import {
  OfferAssistantMessage,
  OfferAssistantRecommendation,
} from "../../types";

/** The suggested-prompt chips, in display order (i18n keys under `offerAssistant.suggestions`). */
export const SUGGESTED_PROMPTS = [
  "normalize",
  "best",
  "leadTimes",
  "expiring",
  "priceDifferences",
] as const;

/** Matches the backend limit on a question's length. */
export const MAX_QUESTION_LENGTH = 4000;

/** What the award matrix needs to mark the assistant's picks. */
export type RecommendationMarks = {
  /** offerId → why the assistant recommends it (per material line). */
  reasons: Record<string, string>;
  /** The single supplier that could take every line, when the assistant proposed one. */
  bestSingle: {
    offerIds: string[];
    supplierName: string;
    reason: string;
  } | null;
};

/** The newest finished assistant message that carries a recommendation, if any. */
export const latestRecommendationMessage = (
  messages: OfferAssistantMessage[]
): OfferAssistantMessage | undefined =>
  [...messages]
    .reverse()
    .find(
      (m) => m.role === "ASSISTANT" && m.status === "DONE" && !!m.recommendation
    );

/**
 * What a recommendation pre-selects ("Aplicar selección" and the automatic pre-selection): the
 * per-material picks; when there are none (e.g. "¿y si quiero un solo proveedor?"), the best
 * single supplier's offers (one per order).
 */
export const recommendedOfferIds = (
  recommendation: OfferAssistantRecommendation
): string[] =>
  recommendation.items.length > 0
    ? recommendation.items.map((item) => item.offerId)
    : (recommendation.bestSingleSupplier?.offerIds ?? []);

/** Both per-material picks and a single-supplier alternative: the card offers each separately. */
export const hasSingleSupplierAlternative = (
  recommendation: OfferAssistantRecommendation
): boolean =>
  recommendation.items.length > 0 &&
  (recommendation.bestSingleSupplier?.offerIds.length ?? 0) > 0;

export const recommendationMarks = (
  recommendation: OfferAssistantRecommendation | null
): RecommendationMarks | null => {
  if (!recommendation) return null;
  const reasons: Record<string, string> = {};
  recommendation.items.forEach((item) => {
    reasons[item.offerId] = item.reason;
  });
  const single = recommendation.bestSingleSupplier;
  // Single-supplier-only answer: its offers are the pre-selected cells, so mark them too.
  if (recommendation.items.length === 0 && single) {
    single.offerIds.forEach((offerId) => {
      reasons[offerId] = single.reason;
    });
  }
  return {
    reasons,
    bestSingle:
      single && single.offerIds.length > 0
        ? {
            offerIds: single.offerIds,
            supplierName: single.supplierName,
            reason: single.reason,
          }
        : null,
  };
};
